import { useState } from 'react'
import Portrait from '../Portrait'
import Dossier from './Dossier'
import { displayPosition, positionFill } from '../board/positions'
import type { NewsItem, PlayerSummary } from '../../types/protocol'

// Pinned to the bottom edge, where a hand already holding a phone is. It only
// appears once something is selected — an empty bar would just eat the last
// two rows of the pool for nothing.
//
// When it isn't your turn the same bar still does useful work: the primary
// action becomes queueing, which is the whole job of the waiting state. And
// when you can do neither, it is still the only dossier affordance below lg —
// which is why a spectator gets the bar with no action row rather than no bar.

interface Props {
  player: PlayerSummary | null
  canPick: boolean
  canQueue: boolean
  queued: boolean
  submitting: boolean
  queueBusy: boolean
  error: string | null
  autopickOn: boolean
  news: NewsItem[]
  newsLoading: boolean
  newsUnavailable: boolean
  onPick: () => void
  onQueue: () => void
  onUnqueue: () => void
  onDismiss: () => void
}

export default function PickBar({
  player,
  canPick,
  canQueue,
  queued,
  submitting,
  queueBusy,
  error,
  autopickOn,
  news,
  newsLoading,
  newsUnavailable,
  onPick,
  onQueue,
  onUnqueue,
  onDismiss,
}: Props) {
  // Deliberately not reset when `player` changes: the drawer is a mode you are
  // in while browsing, not a per-player state. That mode survives a dismiss too
  // — Draft.tsx renders <PickBar> unconditionally, so the `!player` early
  // return below hands React null without unmounting anything, and `expanded`
  // is still here when the next selection arrives. Both halves are asserted in
  // pickBar.test.tsx. If a dismissed drawer should come back closed, reset it
  // explicitly; do not rely on an unmount that never happens.
  const [expanded, setExpanded] = useState(false)

  if (!player) return null

  return (
    // shadow-float casts *upward*, so the bar reads as lifted off the bottom
    // edge with the pool running underneath it rather than ending at it.
    <div
      className={`sticky bottom-0 z-30 border-t bg-surface-2/95 shadow-float backdrop-blur-sm ${
        canPick ? 'border-turn/40' : 'border-line'
      }`}
    >
      <div className="mx-auto w-full max-w-7xl px-3 pt-2.5 pb-3 sm:px-5">
        {/* First in DOM order so the drawer grows *upward* out of the bar,
            which is already pinned where the thumb is.

            Both dossier containers mount at all times and CSS decides which is
            visible, for the same reason the room's panels do: a JS media query
            would remount on resize and throw the pool's scroll and filter state
            away. At lg the scouting column owns the dossier, so this half is
            lg:hidden regardless of `expanded`.

            max-h-[45vh] overflow-y-auto is load-bearing, not defensive: a long
            injury_notes plus a few news items pushes the Draft button off the
            bottom of a phone held in landscape, at the exact moment the clock
            is running. */}
        <div
          id="player-dossier-drawer"
          className={`${expanded ? 'block' : 'hidden'} max-h-[45vh] overflow-y-auto border-b border-line pb-2.5 lg:hidden`}
        >
          <Dossier
            player={player}
            news={news}
            newsLoading={newsLoading}
            newsUnavailable={newsUnavailable}
            newsLimit={3}
          />
        </div>

        {/* A full-width labelled handle rather than a glyph in the identity
            row. A bare ▴ said nothing about what it did, and at 21×24px it
            was under the 44px touch minimum on the one surface that is only
            ever touched. Labelling it in place was the obvious fix and the
            wrong one: the row's only flexible child is the player's name, so
            the text would have come straight out of the thing the bar exists
            to show — a long name truncates at ~102px instead of ~164px.

            Sits above the identity row because that is the bar's top edge,
            where the drawer emerges and where a sheet's grab handle belongs.
            The chevron is aria-hidden: the visible text is the accessible
            name, so voice control matches what a user can actually read. */}
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls="player-dossier-drawer"
          className="mb-2 flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-md border border-line bg-surface-3/60 text-[11px] font-semibold tracking-wide text-ink-mid hover:border-ink-faint hover:text-ink-hi lg:hidden"
        >
          {expanded ? 'Less info' : 'More info'}
          <span aria-hidden="true">{expanded ? '▾' : '▴'}</span>
        </button>

        <div className="flex items-center gap-2.5">
          <Portrait
            fullName={player.full_name}
            position={player.position}
            nflTeam={player.nfl_team}
            src={player.portrait_url}
            size="md"
          />
          <span
            className={`${positionFill(player.position)} text-pos-ink w-8 shrink-0 rounded-sm py-0.5 text-center font-mono text-[10px] font-semibold`}
          >
            {displayPosition(player.position)}
          </span>
          <span className="min-w-0 flex-1 truncate font-display text-lg leading-tight font-bold text-ink-hi">
            {player.full_name}
          </span>
          <span className="shrink-0 font-mono text-[10.5px] text-ink-lo tabular-nums">
            {player.nfl_team ?? 'FA'} · #{player.effective_rank}
          </span>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Clear selection"
            className="shrink-0 px-1 text-ink-lo hover:text-ink-hi"
          >
            ✕
          </button>
        </div>

        {(canPick || canQueue) && (
          <div className="mt-2 flex gap-2">
            {canPick ? (
              <>
                <button
                  type="button"
                  onClick={onPick}
                  disabled={submitting}
                  className="flex-1 rounded-md bg-signal py-3 text-sm font-bold text-ink shadow-raised hover:bg-signal-dim disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? 'Drafting…' : `Draft ${player.full_name}`}
                </button>
                {canQueue && !queued && (
                  <button
                    type="button"
                    onClick={onQueue}
                    disabled={queueBusy}
                    className="shrink-0 rounded-md border border-line-strong bg-surface-3 px-4 text-sm font-semibold text-ink-mid shadow-raised hover:border-ink-faint hover:text-ink-hi disabled:opacity-40"
                  >
                    Queue
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={queued ? onUnqueue : onQueue}
                disabled={queueBusy}
                className={`flex-1 rounded-md py-3 text-sm font-bold shadow-raised disabled:opacity-40 ${
                  queued
                    ? 'border border-line-strong bg-surface-3 text-ink-mid hover:text-ink-hi'
                    : 'bg-signal text-ink hover:bg-signal-dim'
                }`}
              >
                {queued ? `Remove ${player.full_name} from queue` : `Queue ${player.full_name}`}
              </button>
            )}
          </div>
        )}

        {error ? (
          <p className="mt-2 text-center text-xs text-crit">{error}</p>
        ) : canPick && autopickOn ? (
          <p className="mt-2 text-center text-[11px] text-ink-lo">
            Autopick takes your queue&apos;s top name at 0:00
          </p>
        ) : null}
      </div>
    </div>
  )
}
